// M146 Fase 3A — /kanalen — 3-kaart-grid (GSC, GA4, Vlog) in warm-wit stijl.
// M147.1 — Koppelen-knoppen wired to real flows:
//   - GSC/GA4: direct de bestaande /api/auth/google/services/start endpoint,
//     dan window.location.href = authorization_url.
//   - Vlog: info-modal (LUMA-key is beheerder-side, geen user-OAuth).
// Meta/TikTok/LinkedIn zijn per M143 uit de UI verwijderd.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import { PageShell } from "@/components/ui/PageShell";
import { StatusBadge } from "@/components/ui/StatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, LineChart, Video, Loader2, ExternalLink, X, MessageCircleQuestion, Megaphone } from "lucide-react";

// Mapping van backend-status → StatusBadge-variant.
const STATUS_VARIANT = {
  connected_ready:          "gekoppeld",
  configured:               "gekoppeld",
  insufficient_permissions: "actie-nodig",
  verify_failed:            "actie-nodig",
  error:                    "fout",
  not_connected:            "niet-gekoppeld",
};

// Google service key voor OAuth-scopes. Vlog is admin-side (LUMA).
const CARDS = [
  {
    platform: "gsc",
    google_scope: "gsc",
    label: "Google Search Console",
    icon: Search,
    what: "Zoekprestaties: impressies, klikken, posities en top-zoekwoorden per pagina.",
    why: "Om te zien op welke zoekwoorden je vindbaar bent en waar de meeste winst te halen valt.",
  },
  {
    platform: "ga4",
    google_scope: "ga4",
    label: "Google Analytics 4",
    icon: LineChart,
    what: "Websitebezoek: sessies, actieve gebruikers, betrokkenheid en conversies.",
    why: "Om te weten welke pagina's daadwerkelijk bezoekers en aanvragen opleveren.",
  },
  {
    platform: "vlog",
    google_scope: null, // admin-side
    label: "Vlog-generator",
    icon: Video,
    what: "Korte videoscripts en video's die JustEasySEO voor je maakt.",
    why: "Zodat je zichtbaar blijft op social zonder zelf te hoeven filmen of monteren.",
  },
];

function VlogAdminModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
      onClick={onClose}
      data-testid="kanaal-vlog-admin-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vlog-modal-title"
    >
      <div
        className="bg-bg-panel rounded-[16px] max-w-md w-full p-6"
        style={{ boxShadow: "0 10px 40px rgba(23,44,50,0.16)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-11 h-11 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
            <Video className="w-5 h-5 text-accent-teal" aria-hidden="true" />
          </div>
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="text-text-muted hover:text-text-primary p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 id="vlog-modal-title" className="text-lg font-bold text-text-primary mb-2">
          Vlog-generator activeren
        </h3>
        <p className="text-sm text-text-primary leading-relaxed mb-3">
          Deze koppeling wordt door je JustEasySEO-beheerder geactiveerd.
        </p>
        <p className="text-sm text-text-muted leading-relaxed">
          Neem contact op via de knop <span className="text-text-primary font-medium">"Vraag je marketingadviseur"</span> rechtsonder in het scherm.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="kanaal-vlog-admin-modal-close"
            className="rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim"
            style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
          >
            Begrepen
          </button>
        </div>
      </div>
    </div>
  );
}

function ChannelCard({ card, current, onRefresh, onVlogInfo }) {
  const Icon = card.icon;
  const status = current?.status || "not_connected";
  const variant = STATUS_VARIANT[status] || "niet-gekoppeld";
  const isConnected = variant === "gekoppeld";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const disconnect = async () => {
    if (!window.confirm(`Koppeling met ${card.label} verwijderen?`)) return;
    setBusy(true); setErr(null);
    try {
      await api.delete(`/integrations/website/${card.platform}`);
      onRefresh?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Verwijderen mislukt.");
    } finally { setBusy(false); }
  };

  const startGoogleConnect = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.get(
        `/auth/google/services/start?scopes=${card.google_scope}`
      );
      if (r.data?.authorization_url) {
        // Full page-redirect — Google OAuth verwacht dat.
        window.location.href = r.data.authorization_url;
      } else {
        const msg = r.data?.reason_nl
          || "Google-koppeling is nog niet beschikbaar op deze omgeving.";
        toast.error(msg);
        setErr(msg);
        setBusy(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail
        || "Kon Google-koppeling niet starten. Probeer het later opnieuw.";
      toast.error(msg);
      setErr(msg);
      setBusy(false);
    }
  };

  const handleKoppelenClick = () => {
    if (card.platform === "vlog") {
      onVlogInfo?.();
      return;
    }
    startGoogleConnect();
  };

  return (
    <div
      data-testid={`kanaal-card-${card.platform}`}
      className="rounded-[16px] p-6 bg-bg-panel flex flex-col gap-4"
      style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
    >
      {/* Header: icoon + naam + badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-accent-teal" strokeWidth={2} aria-hidden="true" />
          </div>
          <h3 className="text-base font-bold text-text-primary truncate">{card.label}</h3>
        </div>
        <StatusBadge
          variant={variant}
          testid={`kanaal-status-${card.platform}`}
        />
      </div>

      {/* Beschrijving */}
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-text-muted mb-1">Wat JustEasySEO leest</div>
          <p className="text-sm text-text-primary leading-relaxed">{card.what}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-text-muted mb-1">Waarom</div>
          <p className="text-sm text-text-muted leading-relaxed">{card.why}</p>
        </div>
      </div>

      {/* Verbonden site/account weergave */}
      {isConnected && current?.site_url && (
        <div className="text-sm text-text-muted truncate">
          Verbonden met:{" "}
          <span className="text-text-primary">{current.site_url}</span>
        </div>
      )}

      {err && (
        <div className="text-sm text-warn-amber" data-testid={`kanaal-err-${card.platform}`}>{err}</div>
      )}

      {/* CTA — footer */}
      <div className="mt-auto pt-2 flex items-center gap-2">
        {isConnected ? (
          <button
            onClick={disconnect}
            disabled={busy}
            data-testid={`kanaal-disconnect-${card.platform}`}
            className="text-sm text-text-muted hover:text-warn-amber px-3 py-2 disabled:opacity-50"
          >
            {busy ? "Bezig…" : "Ontkoppelen"}
          </button>
        ) : (
          <button
            onClick={handleKoppelenClick}
            disabled={busy}
            data-testid={`kanaal-connect-${card.platform}`}
            className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim transition-colors disabled:opacity-60"
            style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
            Koppelen
            {!busy && card.platform !== "vlog" && <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />}
            {!busy && card.platform === "vlog" && <MessageCircleQuestion className="w-3.5 h-3.5" aria-hidden="true" />}
          </button>
        )}
      </div>
    </div>
  );
}

function KanalenBody() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showVlogModal, setShowVlogModal] = useState(false);
  const [oauthErr, setOauthErr] = useState(null);

  // M148 — als Google een fout terugstuurt (bijv. 'access_denied' bij een
  // account dat niet in de Testing-users-lijst staat), leidt de callback
  // terug naar /kanalen?google_error=... Toon dan een concrete B1-NL uitleg
  // ipv een kale 403 op Google's eigen pagina.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ge = params.get("google_error");
    if (ge) {
      setOauthErr(ge);
      // Ruim de query weg zodat een refresh 'm niet opnieuw toont.
      const url = new URL(window.location.href);
      url.searchParams.delete("google_error");
      url.searchParams.delete("google_error_reason");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/website");
      setData(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kanalen niet beschikbaar.");
    }
  };
  useEffect(() => { load(); }, []);

  const byPlatform = {};
  (data?.items || []).forEach((i) => { byPlatform[i.platform] = i; });

  if (!data && !err) {
    return (
      <div className="flex items-center gap-2 text-text-muted"
            role="status" aria-live="polite" data-testid="kanalen-loading">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Kanalen laden…
      </div>
    );
  }

  return (
    <>
      {oauthErr && (
        <div
          className="mb-5 rounded-[16px] p-5 border"
          style={{ background: "rgba(138,91,19,0.06)", borderColor: "rgba(138,91,19,0.28)" }}
          role="alert"
          data-testid="kanalen-oauth-err"
        >
          <div className="text-sm font-bold text-text-primary mb-1">
            Google heeft je koppeling nog niet toegestaan
          </div>
          <p className="text-sm text-text-primary leading-relaxed">
            De koppeling met Google is nog niet geopend voor jouw Google-account.
            Dit wordt ingesteld door je JustEasySEO-beheerder. Zij voegen je
            e-mailadres eenmalig toe aan de Google-toegangslijst — daarna werkt
            koppelen gewoon.
          </p>
          <p className="text-sm text-text-muted mt-2">
            Neem contact op via de knop{" "}
            <span className="text-text-primary font-medium">"Vraag je marketingadviseur"</span>{" "}
            rechtsonder in het scherm. Zij hebben je e-mailadres nodig en regelen
            de rest.
          </p>
        </div>
      )}
      {err && (
        <div className="mb-4 text-sm text-warn-amber" data-testid="kanalen-err">{err}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
            data-testid="kanalen-grid">
        {CARDS.map((c) => (
          <ChannelCard
            key={c.platform}
            card={c}
            current={byPlatform[c.platform]}
            onRefresh={load}
            onVlogInfo={() => setShowVlogModal(true)}
          />
        ))}
        {/* M148 — Ads-uitvoerings-kaart (informatief, geen koppeling nodig) */}
        <div
          data-testid="kanaal-card-google-ads-execution"
          className="rounded-[16px] p-6 bg-bg-panel flex flex-col gap-4"
          style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
                <Megaphone className="w-5 h-5 text-accent-teal" strokeWidth={2} aria-hidden="true" />
              </div>
              <h3 className="text-base font-bold text-text-primary truncate">
                Google Ads (uitvoering)
              </h3>
            </div>
            <StatusBadge variant="voorbereid" label="Nog niet nodig"
                          testid="kanaal-status-google-ads-execution" />
          </div>
          <p className="text-sm text-text-primary leading-relaxed">
            JustEasySEO maakt kant-en-klare voorstellen voor je Google
            Ads-campagnes. Je past ze zelf toe in Google Ads.
          </p>
          <p className="text-sm text-text-muted leading-relaxed">
            Zo houd je zelf de knop op alle wijzigingen. Wil je later
            automatisch laten toepassen? Laat het je adviseur weten.
          </p>
          <div className="mt-auto pt-2">
            <a
              href="/content?tab=advertenties"
              data-testid="kanaal-ads-execution-view-btn"
              className="inline-flex items-center gap-1.5 text-sm text-accent-teal-dim hover:text-accent-teal"
            >
              Bekijk je voorstellen →
            </a>
          </div>
        </div>
      </div>

      {data?.note_nl && (
        <p className="mt-6 text-sm text-text-muted italic" data-testid="kanalen-note">
          {data.note_nl}
        </p>
      )}

      {showVlogModal && <VlogAdminModal onClose={() => setShowVlogModal(false)} />}
    </>
  );
}

export default function IntegrationsPage() {
  const { embedded } = useEmbed();
  if (embedded) return <KanalenBody />;
  return (
    <AppLayout>
      <PageShell
        eyebrow="JOUW KANALEN"
        title="Kanalen"
        subtitle="Koppel Google Search Console, Analytics en de vlog-generator. Meer kanalen volgen."
      >
        <KanalenBody />
      </PageShell>
    </AppLayout>
  );
}
